import { Link } from "react-router-dom";
import { Instagram, Music2 } from "lucide-react";

const footerColumns = [
  {
    title: "Shop",
    links: [
      { to: "/category/living", label: "Furniture" },
      { to: "/category/lighting", label: "Lighting" },
      { to: "/category/decor", label: "Decor" },
      { to: "/category/area-rugs", label: "Rugs" },
    ],
  },
  {
    title: "About",
    links: [
      { to: "/about", label: "Our Story" },
      { to: "/sustainability", label: "Sustainability" },
    ],
  },
  {
    title: "Customer Care",
    links: [
      { to: "/contact", label: "Contact Us" },
      { to: "/shipping", label: "Shipping & Delivery" },
      { to: "/returns", label: "Returns" },
      { to: "/faq", label: "FAQ" },
      // The page is Order Help: there is no order tracking to offer.
      { to: "/order-tracking", label: "Order Help" },
    ],
  },
  {
    title: "Resources",
    links: [
      { to: "/ideas", label: "Ideas & Inspiration" },
      { to: "/design-services", label: "Design Services" },
      { to: "/tools", label: "Sizing Tools" },
    ],
  },
];

// Socials — Instagram and TikTok use verified branded handles.
// Pinterest and Facebook profiles are hidden until custom usernames are set
// (raw numeric/profile-id URLs undermine trust — audit finding G5/CR-2).
const socials = [
  { Icon: Instagram, href: "https://www.instagram.com/loftloomfurniture/", label: "Instagram" },
  { Icon: Music2, href: "https://www.tiktok.com/@loftloomfurniture", label: "TikTok" },
];

export const Footer = ({ hideTrustBar = false }: { hideTrustBar?: boolean }) => {
  return (
    <footer className="bg-sand/40 border-t border-border text-foreground">
      {/* DESKTOP layout */}
      <div className="hidden md:grid w-[89%] max-w-[1280px] mx-auto pt-14 pb-8 grid-cols-10 gap-6">
        {/* Brand */}
        <div className="col-span-2">
          <Link to="/" className="block leading-none">
            <span className="font-display block text-[28px] tracking-[0.04em] uppercase text-foreground">
              LoftLoom
            </span>
            <span className="block text-[9px] tracking-[0.5em] uppercase font-semibold text-foreground/80 mt-1 ml-0.5">
              Interior
            </span>
          </Link>
          <p className="text-[12px] text-muted-foreground leading-relaxed mt-4 max-w-[200px]">
            Timeless furniture and décor curated for inspired living.
          </p>
          <div className="flex items-center gap-3 mt-5 text-foreground/70" aria-label="Social links">
            {socials.map(({ Icon, href, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="w-8 h-8 rounded-full border border-foreground/25 flex items-center justify-center hover:bg-foreground hover:text-background hover:border-foreground transition-colors"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>

        {/* Link columns */}
        {footerColumns.map((column) => (
          <div key={column.title} className="col-span-2">
            <h3 className="text-[11px] tracking-[0.22em] uppercase font-bold text-foreground mb-4">
              {column.title}
            </h3>
            <ul className="space-y-2">
              {column.links.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    className="text-[13px] text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}

      </div>

      {/* MOBILE layout — elegant, vertically structured */}
      <div className="md:hidden w-[89%] max-w-[1280px] mx-auto pt-12 pb-6">
        {/* Brand block — centered */}
        <div className="text-center">
          <Link to="/" className="inline-block leading-none">
            <span className="font-display block text-[32px] tracking-[0.05em] uppercase text-foreground">
              LoftLoom
            </span>
            <span className="block text-[10px] tracking-[0.5em] uppercase font-semibold text-foreground/80 mt-1.5">
              Interior
            </span>
          </Link>
          <div className="mx-auto mt-4 h-px w-10 bg-foreground/25" />
          <p className="text-[13px] text-muted-foreground leading-relaxed mt-4 max-w-[280px] mx-auto">
            Timeless furniture and décor curated for inspired living.
          </p>
          <div className="flex items-center justify-center gap-3 mt-5 text-foreground/70" aria-label="Social links">
            {socials.map(({ Icon, href, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="w-10 h-10 rounded-full border border-foreground/25 flex items-center justify-center hover:bg-foreground hover:text-background hover:border-foreground transition-colors"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>

        {/* Link columns — 2-up, clean */}
        <div className="mt-10 pt-8 border-t border-border/70 grid grid-cols-2 gap-y-8 gap-x-5">
          {footerColumns.map((column) => (
            <div key={column.title}>
              <h3 className="text-[10px] tracking-[0.28em] uppercase font-bold text-foreground/80 mb-3">
                {column.title}
              </h3>
              <ul className="space-y-2.5">
                {column.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      className="text-[13px] text-muted-foreground hover:text-primary transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-border">
        <div className="w-[89%] max-w-[1280px] mx-auto py-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-muted-foreground">
          <span className="text-center">© {new Date().getFullYear()} LoftLoom Interior. All Rights Reserved.</span>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-foreground transition-colors">
              Privacy Policy
            </Link>
            <span className="text-foreground/30">|</span>
            <Link to="/terms" className="hover:text-foreground transition-colors">
              Terms & Conditions
            </Link>
            <span className="text-foreground/30">|</span>
            <Link to="/accessibility" className="hover:text-foreground transition-colors">
              Accessibility
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile bottom nav spacer */}
      <div className="h-14 md:hidden" />
    </footer>
  );
};
